import React, { ReactNode, useMemo, useState } from "react";
import KC_IconContainer from "../../../../../designsystem/KC_IconContainer/KC_IconContainer";
import {
  type Config,
  type Cta,
  getDefaultGain,
  getDefaultIcon,
  TokenHistoryLabel
} from "kadow-jetpack-model";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import KC_Input from "../../../../../designsystem/KC_FormInput/KC_Input";
import KC_Tooltip from "../../../../../designsystem/KC_Tooltip/KC_Tooltips";
import { tt } from "../../../../../utils/i18n/i18n";
import getDefaultTitleForCTA from "../../../../../utils/helpers/getDefaultTitleForCTA";
import { BASE_URL } from "../../../../../utils/constants";
import { isRequiredCtas } from "../../../../../utils/helpers/isRequiredCtas";
import sortCtasWithRepeatableAtEnd from "../../../../../utils/helpers/sortCtasWithRepeatableAtEnd";

const generateRandomId = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};

interface Props {
  ctaId: TokenHistoryLabel;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched?: (field: string, value: any) => void;
  config: Config;
  isSelected?: boolean;
  customId?: string;
}

export default function CTACard(props: Props) {
  const [showTooltip, setShowTooltip] = useState(false);

  const getTooltipText = (ctaId: TokenHistoryLabel): ReactNode => {
    switch (ctaId) {
      case TokenHistoryLabel.Receipt:
        return tt("cta_card_tooltip_receipt");
      case TokenHistoryLabel.Share:
        return tt("cta_card_tooltip_share");
      case TokenHistoryLabel.BAO:
        return tt("cta_card_tooltip_bao");
      case TokenHistoryLabel.CheckIn:
        return tt("cta_card_tooltip_checkin");
      case TokenHistoryLabel.WhatsappGroup:
        return (
          <div className="d-flex flex-column sm-gap">
            <KC_UIText
              weight="reg"
              text={tt("cta_card_tooltip_whatsappgroup")}
              className="text-center"
            />
            <div>&nbsp;</div>
            <KC_UIText
              weight="reg"
              text={tt("cta_card_tooltip_whatsappgroup_1")}
              className="text-center"
            />
            <KC_UIText
              weight="reg"
              text={tt("cta_card_tooltip_whatsappgroup_2")}
              className="text-center"
            />
            <KC_UIText
              weight="reg"
              text={tt("cta_card_tooltip_whatsappgroup_3")}
              className="text-center"
            />
            <KC_UIText
              weight="reg"
              text={tt("cta_card_tooltip_whatsappgroup_4")}
              className="text-center"
            />
          </div>
        );
      default:
        return "";
    }
  };

  const isShareDisabledByBea =
    props.ctaId === TokenHistoryLabel.Share && !!props.config.bea;

  const handleSelectCTA = () => {
    // Prevent adding CustomForm CTA - it can only be displayed, not added
    if (props.ctaId === TokenHistoryLabel.CustomForm && !props.isSelected) {
      return;
    }

    if (isShareDisabledByBea && !props.isSelected) {
      return;
    }

    if (props.isSelected) {
      // For custom CTAs with customId, filter by customId
      // For other CTAs, filter by id
      const newCTAs =
        props.config.ctas?.filter((c: Cta) => {
          if (props.ctaId === TokenHistoryLabel.Custom && props.customId) {
            return c.customId !== props.customId;
          }
          return c.id !== props.ctaId;
        }) || [];
      props.setFieldValue("ctas", sortCtasWithRepeatableAtEnd(newCTAs));
    } else {
      const cta: Cta = {
        id: props.ctaId,
        title: getDefaultTitleForCTA(props.ctaId),
        icon: getDefaultIcon(props.ctaId),
        gain: getDefaultGain(props.ctaId),
        v:
          props.ctaId === TokenHistoryLabel.Receipt ||
          props.ctaId === TokenHistoryLabel.OnlineOrder
      };

      // Generate unique customId for custom CTAs
      if (props.ctaId === TokenHistoryLabel.Custom) {
        cta.customId = generateRandomId();
      }

      if (props.ctaId === TokenHistoryLabel.Share) {
        cta.sGain = 1;
      }
      if (props.ctaId === TokenHistoryLabel.BAO) {
        cta.nTokensOnPlay = 1;
      }
      if (props.ctaId === TokenHistoryLabel.Receipt) {
        cta.receiptAmountPerQuantum = 1;
      }
      cta.v = false;
      const baseCTAs = props.config.ctas || [];

      if (isRequiredCtas(cta.id)) {
        const filteredCTAs = baseCTAs.filter(
          (existingCta) => existingCta.id !== cta.id
        );
        const newCTAs = [...filteredCTAs, cta];
        props.setFieldValue("ctas", sortCtasWithRepeatableAtEnd(newCTAs));
      } else {
        const insertIndex = baseCTAs.findIndex((cta) => isRequiredCtas(cta.id));

        if (insertIndex === -1) {
          props.setFieldValue(
            "ctas",
            sortCtasWithRepeatableAtEnd([...baseCTAs, cta])
          );
        } else {
          const newCTAs = [
            ...baseCTAs.slice(0, insertIndex),
            cta,
            ...baseCTAs.slice(insertIndex)
          ];
          props.setFieldValue("ctas", sortCtasWithRepeatableAtEnd(newCTAs));
        }
      }
    }
  };

  const shouldShowTooltip = useMemo(() => {
    return (
      props.ctaId === TokenHistoryLabel.Receipt ||
      props.ctaId === TokenHistoryLabel.Share ||
      props.ctaId === TokenHistoryLabel.BAO ||
      props.ctaId === TokenHistoryLabel.CheckIn ||
      props.ctaId === TokenHistoryLabel.WhatsappGroup
    );
  }, [props.ctaId]);

  const isCustomFormUnselected =
    props.ctaId === TokenHistoryLabel.CustomForm && !props.isSelected;

  const cardContent = (
    <div
      key={props.ctaId}
      id={`unselected-${props.ctaId}`}
      className="d-flex flex-column align-items-center st-gap border"
      onClick={handleSelectCTA}
      onMouseEnter={() => shouldShowTooltip && setShowTooltip(true)}
      onMouseLeave={() => shouldShowTooltip && setShowTooltip(false)}
      style={{
        padding: 16,
        borderRadius: 12,
        opacity: isCustomFormUnselected || isShareDisabledByBea ? 0.6 : 1,
        cursor:
          isCustomFormUnselected || isShareDisabledByBea
            ? "not-allowed"
            : "pointer"
      }}
    >
      <div className="d-flex flex-row align-items-center justify-content-between w-100 st-gap">
        <KC_IconContainer
          className="m-0"
          imgSrc={`${BASE_URL}/img/logo/${getDefaultIcon(props.ctaId)}`}
        />

        <KC_UIText
          weight={KC_Weights.bold}
          text={getDefaultTitleForCTA(props.ctaId)}
          className="flex-grow-1"
        />

        <KC_Toggle
          id={`${props.ctaId}-unselected-btn`}
          isChecked={props.isSelected || false}
          onToggle={handleSelectCTA}
          isInactive={isCustomFormUnselected || isShareDisabledByBea}
        />
      </div>

      {isCustomFormUnselected && (
        <KC_UIText
          weight="reg"
          text={tt("cta_custom_form_contact_team")}
          className="text-muted text-center"
        />
      )}

      {isShareDisabledByBea && (
        <KC_UIText
          weight="reg"
          text={tt("cta_disabled_bea")}
          className="text-danger text-center"
        />
      )}

      {props.ctaId === TokenHistoryLabel.CheckIn && (
        <div
          className="d-flex flex-column w-100 justify-content-start st-gap"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="d-flex flex-row w-100 align-items-center st-gap justify-content-start">
            <KC_Input
              disabled={!props.isSelected}
              id="checkInCode"
              type="number"
              htmlFor="cic"
              value={props.config.cic || ""}
              placeholder={tt("cta_card_checkin_code_placeholder")}
              setFieldValue={props.setFieldValue}
              setFieldTouched={props.setFieldTouched}
              required={true}
            />
          </div>
        </div>
      )}

      {props.ctaId === TokenHistoryLabel.Receipt && (
        <>
          <div
            className="d-flex w-100 flex-column gap-xxs"
            onClick={(event) => event.stopPropagation()}
          >
            <KC_UIText
              weight="reg"
              text={tt("cta_card_receipt_keywords_instruction")}
            />
            <div className="d-flex flex-column flex-sm-row gap-sm">
              <KC_Input
                disabled={!props.isSelected}
                id="keyword-1"
                type="text"
                htmlFor="mn.0"
                value={props.config.mn?.[0] || ""}
                placeholder={tt("cta_card_keyword_1")}
                setFieldValue={props.setFieldValue}
                required={true}
              />
              <KC_Input
                disabled={!props.isSelected}
                id="keyword-2"
                type="text"
                htmlFor="mn.1"
                value={props.config.mn?.[1] || ""}
                placeholder={tt("cta_card_keyword_2")}
                setFieldValue={props.setFieldValue}
              />
              <KC_Input
                disabled={!props.isSelected}
                id="keyword-3"
                type="text"
                htmlFor="mn.2"
                value={props.config.mn?.[2] || ""}
                placeholder={tt("cta_card_keyword_3")}
                setFieldValue={props.setFieldValue}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (shouldShowTooltip) {
    return (
      <KC_Tooltip
        text={getTooltipText(props.ctaId)}
        show={showTooltip}
        onToggle={setShowTooltip}
        position="top"
        width="50vw"
        pre={true}
      >
        {cardContent}
      </KC_Tooltip>
    );
  }

  return cardContent;
}
